import React, { useEffect, useState } from 'react';
import { useSkusStore } from '../../../../store/skusStore';
import { useCategoriesStore } from '../../../../store/categoriesStore';
import { Sku, SkuStatus } from '../../../../services/skusApi';
import { ImagePicker } from '../../../../components/ImagePicker';
import styles from './EditProductModal.module.css';

interface EditProductModalProps {
  open: boolean;
  product: Sku | null;
  onClose: () => void;
}

export const EditProductModal: React.FC<EditProductModalProps> = ({ open, product, onClose }) => {
  const [skuCode, setSkuCode] = useState('');
  const [barcode, setBarcode] = useState('');
  const [productName, setProductName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [unitCost, setUnitCost] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [ageRestriction, setAgeRestriction] = useState<'' | '18+' | '21+'>('');
  const [reorderPoint, setReorderPoint] = useState('');
  const [reorderQty, setReorderQty] = useState('');
  const [status, setStatus] = useState<SkuStatus>('active');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const update = useSkusStore((s) => s.update);
  const categories = useCategoriesStore((s) => s.items);
  const fetchCategories = useCategoriesStore((s) => s.fetchList);

  useEffect(() => {
    if (open && product) {
      setSkuCode(product.sku_code);
      setBarcode(product.barcode ?? '');
      setProductName(product.product_name);
      setDescription(product.description || '');
      setCategoryId(product.category_id ? String(product.category_id) : '');
      setUnitCost(String(product.unit_cost));
      setListPrice(product.list_price !== null ? String(product.list_price) : '');
      setAgeRestriction(product.age_restriction ?? '');
      setReorderPoint(String(product.reorder_point ?? 0));
      setReorderQty(String(product.reorder_qty ?? 0));
      setStatus(product.status);
      setImageUrl(product.image_url ?? null);
      setError(null);
      setSubmitting(false);
      if (categories.length === 0) fetchCategories().catch(() => undefined);
    }
  }, [open, product, categories.length, fetchCategories]);

  if (!open || !product) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!skuCode.trim()) { setError('SKU code is required'); return; }
    if (!productName.trim()) { setError('Product name is required'); return; }
    const cost = parseFloat(unitCost);
    if (isNaN(cost) || cost < 0) { setError('Unit cost must be a positive number'); return; }
    const price = listPrice ? parseFloat(listPrice) : null;
    if (price !== null && (isNaN(price) || price < 0)) { setError('List price must be a positive number'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await update(product.id, {
        sku_code: skuCode.trim(),
        barcode: barcode.trim() || null,
        product_name: productName.trim(),
        description: description.trim() || undefined,
        category_id: categoryId ? Number(categoryId) : null,
        unit_cost: cost,
        list_price: price,
        age_restriction: ageRestriction || null,
        status,
        image_url: imageUrl,
        reorder_point: parseInt(reorderPoint, 10) || 0,
        reorder_qty: parseInt(reorderQty, 10) || 0,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update product');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Edit Product</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.row}>
            <label className={styles.field}>
              <span>SKU Code <span className={styles.required}>*</span></span>
              <input type="text" value={skuCode} onChange={(e) => setSkuCode(e.target.value)} required maxLength={50} />
            </label>
            <label className={styles.field}>
              <span>UPC / Barcode</span>
              <input type="text" value={barcode} onChange={(e) => setBarcode(e.target.value)} maxLength={64} placeholder="e.g., 070847811800" />
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as SkuStatus)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </label>
            <div className={styles.field} aria-hidden="true" />
          </div>

          <label className={styles.field}>
            <span>Product Name <span className={styles.required}>*</span></span>
            <input type="text" value={productName} onChange={(e) => setProductName(e.target.value)} required maxLength={200} />
          </label>

          <label className={styles.field}>
            <span>Description</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={500} />
          </label>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Category</span>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Age Restriction</span>
              <select value={ageRestriction} onChange={(e) => setAgeRestriction(e.target.value as '' | '18+' | '21+')}>
                <option value="">None</option>
                <option value="18+">18+</option>
                <option value="21+">21+</option>
              </select>
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Unit Cost <span className={styles.required}>*</span></span>
              <input type="number" min="0" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} required />
            </label>
            <label className={styles.field}>
              <span>List Price</span>
              <input type="number" min="0" step="0.01" value={listPrice} onChange={(e) => setListPrice(e.target.value)} />
            </label>
          </div>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>Reorder Point</span>
              <input type="number" min="0" step="1" value={reorderPoint} onChange={(e) => setReorderPoint(e.target.value)} placeholder="0 = use default" />
            </label>
            <label className={styles.field}>
              <span>Reorder Qty</span>
              <input type="number" min="0" step="1" value={reorderQty} onChange={(e) => setReorderQty(e.target.value)} />
            </label>
          </div>

          <ImagePicker value={imageUrl} onChange={setImageUrl} />

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductModal;
